import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { LEADS_DOWNLOAD_FIELDS } from 'utils/constants';
import { streamDownload } from 'api/leads';
import Box from 'components/Box';
import Clickwrap from 'components/Clickwrap';
import APModal from 'components/Modal/APModal';
import CalendarWidget from 'components/FormFields/CalendarWidget';
import ErrorModal from 'components/Modal/ConfirmationModal';
import { styledTheme } from 'globalStyles/theme';
import './styles.scss';

dayjs.extend(utc);

/**
 * @description: Download Modal Component from ActionButton.
 * @param {*}
 * @return {*}
 */
 export const LeadsDownloadModal = ({
   statuses,
   dismiss,
   downloadIsOpen,
   setDownloadIsOpen,
}) => {
  const userInfo = useSelector((state) => state.global.userInfo);
  const viewOptions = useSelector((state) => state.global.leadsViewOptions);
  const { orgId } = userInfo;

  const clickwrapColor = '#676767';

  // Download parameters.
  const [downloadStatuses, setDownloadStatuses] = useState(statuses.map(status => {
    return {
      id: status.id,
      name: status.name,
      checked: true,
    };
  }));
  const [downloadViewOptions, setDownloadViewOptions] = useState(
    // Looks at visible fields to auto-select download fields.
    LEADS_DOWNLOAD_FIELDS.map(field => {
      const checked = viewOptions.find(option => field.id === option.id)?.checked ?? false;

      return {
        ...field,
        checked,
      };
    }),
  );

  // Download radio states
  const [downloadStatus, setDownloadStatus] = useState('all');
  const [downloadFields, setDownloadFields] = useState('all');
  const [downloadInquiry, setDownloadInquiry] = useState('all');

  // Calendar picker states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Date parameters
  const today = dayjs();
  const startOfMonth = dayjs().startOf('month');
  const lastWeek = dayjs().subtract(7, 'day');
  const lastMonth = dayjs().subtract(1, 'month').format('M') - 1;
  const lastMonthStart = dayjs().month(lastMonth).startOf('month');
  const lastMonthEnd = dayjs().month(lastMonth).endOf('month');

  // Error state
  const [hasError, setHasError] = useState(false);
  const [errorDate, setErrorDate] = useState(false);

  // Preparing status
  const [isPreparing, setIsPreparing] = useState(false);

  // Update statuses if booking process changes.
  useEffect(() => {
    setDownloadStatuses(statuses.map(status => {
      return {
        id: status.id,
        name: status.name,
        checked: true,
      };
    }));
  }, [downloadIsOpen]);

  const download = () => {
    const params = {};

    params.status_label = downloadStatus;

    if (downloadStatus === 'customized') {
      const statusParam = downloadStatuses.filter(status => status.checked)
        .map(status => status.id);

      if (statusParam.length) {
        params.status_ids = statusParam;
      }
    }

    if (downloadFields === 'customize') {
      const fieldsParam = downloadViewOptions.filter(field => field.checked)
        .reduce((prev, field) => prev.concat(field.param), []);

      if (fieldsParam.length) {
        params.fields = fieldsParam;
      }
    }

    if (downloadInquiry !== 'all') {
      let start_date;
      let end_date;
      switch (downloadInquiry) {
        case 'currentMonth':
          start_date = startOfMonth.format('YYYY-MM-DD');
          end_date = today.format('YYYY-MM-DD');
          break;
        case 'lastWeek':
          start_date = lastWeek.format('YYYY-MM-DD');
          end_date = today.format('YYYY-MM-DD');
          break;
        case 'lastMonth':
          start_date = lastMonthStart.format('YYYY-MM-DD');
          end_date = lastMonthEnd.format('YYYY-MM-DD');
          break;
        case 'custom':
          start_date = dayjs.utc(startDate).format('YYYY-MM-DD');
          end_date = dayjs.utc(endDate).format('YYYY-MM-DD');
          break;
        default:
          start_date = dayjs(startOfMonth).format('YYYY-MM-DD');
          end_date = dayjs(today).format('YYYY-MM-DD');
      }

      if (downloadInquiry === 'custom' && !dayjs(endDate).isAfter(dayjs(startDate))) {
        setErrorDate(true);
        return;
      }

      params.start_date = start_date;
      params.end_date = end_date;
    }

    setErrorDate(false);
    setIsPreparing(true);

    streamDownload({ orgId, params })
      .then(response => {
        if(response.status !== 401) window.location.replace(response.data.url);
        setIsPreparing(false);
        dismiss();
        setDownloadIsOpen(false);
      })
      .catch(() => {
        setIsPreparing(false);
        setHasError(true);
      });
  };

  const errorCloseHandler = () => {
    setHasError(false);
  };

  const DownloadOptions = () => {
    const secondStatusColumnStart = Math.ceil(downloadStatuses.length / 2);
    const secondColumnStart = Math.ceil(downloadViewOptions.length / 2);

    const onDownloadStatusChange = (id) => {
      const newDownloadStatuses = downloadStatuses.map(status => {
        if (status.id === id) {
          return { ...status, checked: !status.checked };
        }
        return status;
      });
      setDownloadStatuses(newDownloadStatuses);
    };

    const onDownloadCheckChange = (index) => {
      const newDownloadOptions = [...downloadViewOptions];
      const { checked } = downloadViewOptions[index];
      newDownloadOptions[index].checked = !checked;
      setDownloadViewOptions(newDownloadOptions);
    };

    return (
      <div className="download-options text-left">
        <div className="download-options__field">
          <label className="field-title">Select</label>
          <div className="d-flex form-item form-item--radio">
            <div className="form-check form-check--inline">
              <input
                id="status-all-radio"
                name="status-all"
                type="radio"
                value="all"
                checked={downloadStatus === 'all'}
                onChange={() => { setDownloadStatus('all'); }}
              />
              <label htmlFor="status-all-radio"
                className="form-check-label"
              >
                All
              </label>
            </div>
            <div className="form-check form-check--inline">
              <input
                id="status-current-radio"
                name="status-current"
                type="radio"
                value="current"
                checked={downloadStatus === 'current'}
                onChange={() => { setDownloadStatus('current'); }}
              />
              <label htmlFor="status-current-radio"
                className="form-check-label"
              >
                Current
              </label>
            </div>
            <div className="form-check form-check--inline">
              <input
                id="status-customize-radio"
                name="status-customize"
                type="radio"
                value="customized"
                checked={downloadStatus === 'customized'}
                onChange={() => { setDownloadStatus('customized'); }}
              />
              <label htmlFor="status-customize-radio"
                className="form-check-label"
              >
                Customize
              </label>
            </div>
          </div>
        </div>
        {downloadStatus === 'customized' && (
          <div className="download-parameters download-parameters--statuses">
            <div className="download-parameters__column">
              {downloadStatuses.slice(0, secondStatusColumnStart).map(option => {
                return (
                  <div
                    key={option.id}
                    className="download-parameter view-option view-option--download"
                  >
                    <Clickwrap
                      name={`download-status--${option.id}`}
                      checked={option.checked}
                      className="clickwrap--view-option download-parameter__checkbox small"
                      onChange={() => onDownloadStatusChange(option.id)}
                      color={clickwrapColor}
                    />
                    <label
                      htmlFor={`download-status--${option.id}`}
                    >
                      {option.name}
                    </label>
                  </div>
                );
              })}
            </div>
            <div className="download-parameters__column">
              {downloadStatuses.slice(secondStatusColumnStart).map(option => {
                return (
                  <div
                    key={option.id}
                    className="download-parameter view-option view-option--download"
                  >
                    <Clickwrap
                      name={`download-status--${option.id}`}
                      checked={option.checked}
                      className="clickwrap--view-option download-parameter__checkbox small"
                      onChange={() => onDownloadStatusChange(option.id)}
                      color={clickwrapColor}
                    />
                    <label
                      htmlFor={`download-status--${option.id}`}
                    >
                      {option.name}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="download-options__field">
          <label className="field-title">Fields</label>
          <div className="d-flex form-item form-item--radio">
            <div className="form-check form-check--inline">
              <input
                id="fields-all-radio"
                name="fields-all"
                type="radio"
                value="fields-all"
                checked={downloadFields === 'all'}
                onChange={() => { setDownloadFields('all'); }}
              />
              <label htmlFor="fields-all-radio"
                className="form-check-label"
              >
                All
              </label>
            </div>
            <div className="form-check form-check--inline">
              <input
                id="fields-customize-radio"
                name="customize"
                type="radio"
                value="customize"
                checked={downloadFields === 'customize'}
                onChange={() => { setDownloadFields('customize'); }}
              />
              <label htmlFor="fields-customize-radio"
                className="form-check-label"
              >
                Customize
              </label>
            </div>
          </div>
        </div>
        {downloadFields === 'customize' && (
          <div className="download-parameters download-parameters--fields">
            <div className="download-parameters__column">
              {downloadViewOptions.slice(0, secondColumnStart).map((option, key) => {
                return (
                  <div
                    key={option.id}
                    className="download-parameter view-option view-option--download"
                  >
                    <Clickwrap
                      name={`download-field--${option.id}`}
                      checked={option.checked}
                      className="clickwrap--view-option download-parameter__checkbox small"
                      onChange={() => onDownloadCheckChange(key)}
                      color={clickwrapColor}
                    />
                    <label
                      htmlFor={`download-field--${option.id}`}
                    >
                      {option.name}
                    </label>
                  </div>
                );
              })}
            </div>
            <div className="download-parameters__column">
              {downloadViewOptions.slice(secondColumnStart).map((option, key) => {
                return (
                  <div
                    key={option.id}
                    className="download-parameter view-option view-option--download"
                  >
                    <Clickwrap
                      name={`download-field--${option.id}`}
                      checked={option.checked}
                      className="clickwrap--view-option download-parameter__checkbox small"
                      onChange={() => onDownloadCheckChange(secondColumnStart + key)}
                      color={clickwrapColor}
                    />
                    <label
                      htmlFor={`download-field--${option.id}`}
                    >
                      {option.name}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="download-options__field">
          <label className="field-title">Lead Inquiry Date</label>
          <div className="form-item form-item--radio">
            <div className="form-check">
              <input
                id="inquiry-all-radio"
                name="inquiry-all"
                type="radio"
                value="inquiry-all"
                checked={downloadInquiry === 'all'}
                onChange={() => { setDownloadInquiry('all'); }}
              />
              <label htmlFor="inquiry-all-radio"
                className="form-check-label"
              >
                All
              </label>
            </div>
            <div className="form-check">
              <input
                id="inquiry-curr-month-radio"
                name="current month"
                type="radio"
                value="current-month"
                checked={downloadInquiry === 'currentMonth'}
                onChange={() => { setDownloadInquiry('currentMonth'); }}
              />
              <label htmlFor="inquiry-curr-month-radio"
                className="form-check-label"
              >
                Current Month
                <span className="range">
                  &nbsp;&nbsp;({startOfMonth.format('MMMM D')} - NOW)
                </span>
              </label>
            </div>
            <div className="form-check">
              <input
                id="inquiry-week-radio"
                name="last week"
                type="radio"
                value="last-week"
                checked={downloadInquiry === 'lastWeek'}
                onChange={() => { setDownloadInquiry('lastWeek'); }}
              />
              <label htmlFor="inquiry-week-radio"
                className="form-check-label"
              >
                Last 7 Days
                <span className="range">
                  &nbsp;&nbsp;({lastWeek.format('MMMM D')} - {today.format('MMMM D')})
                </span>
              </label>
            </div>
            <div className="form-check">
              <input
                id="inquiry-month-radio"
                name="last month"
                type="radio"
                value="last-month"
                checked={downloadInquiry === 'lastMonth'}
                onChange={() => { setDownloadInquiry('lastMonth'); }}
              />
              <label htmlFor="inquiry-month-radio"
                className="form-check-label"
              >
                Last Month
                <span className="range">
                  &nbsp;&nbsp;({lastMonthStart.format('MMMM D')} - {lastMonthEnd.format('MMMM D')})
                </span>
              </label>
            </div>
            <div className="form-check">
              <input
                id="inquiry-custom-radio"
                name="custom"
                type="radio"
                value="custom"
                checked={downloadInquiry === 'custom'}
                onChange={() => { setDownloadInquiry('custom'); }}
              />
              <label htmlFor="inquiry-custom-radio"
                className="form-check-label"
              >
                Custom
              </label>
            </div>
          </div>
          {downloadInquiry === 'custom' && (
            <div className="inquiry-custom-dates">
              <CalendarWidget
                name=""
                date={startDate}
                setDate={setStartDate}
                showLabel="false"
                showError={errorDate}
                placeholder="XX/XX/XXXX"
                defaultPlacement="top-start"
              />
              <span className="dash">&mdash;</span>
              <CalendarWidget
                name=""
                date={endDate}
                setDate={setEndDate}
                showLabel="false"
                showError={errorDate}
                placeholder="XX/XX/XXXX"
                defaultPlacement="top-start"
              />
            </div>
          )}
          {errorDate && (
            <Box>
              <p
                style={{
                  color: styledTheme.errorColor,
                  fontSize: '12px',
                  marginTop: '1rem',
                  textAlign: 'center',
                }}
              >
                Left date in date range should come before right date.
              </p>
            </Box>
          )}
        </div>
      </div>
    );
  };

  const DownloadAction = () => {
    return (
      <button
        className={clsx('button', {
          disabled: isPreparing,
          preparing: isPreparing,
        })}
        onClick={download}
      >
        {!isPreparing && (
          <span>Download CSV</span>
        )}
        {isPreparing && (
          <>
            <span className="visually-hidden--mobile">Downloading</span>
            <span className="fa fa-spin fa-spinner" />
          </>
        )}
      </button>
    );
  };

  return (
    <>
      <APModal
        open={downloadIsOpen}
        onClose={() => setDownloadIsOpen(false)}
        name="leads-download"
        title="Download"
        actions={DownloadAction()}
        size="narrow"
      >
        {DownloadOptions()}
      </APModal>
      {hasError && (
        <ErrorModal
          visible={hasError}
          title="Something went wrong."
          subtitle="The dates selected may not have any lead records to download.
                  Please try different dates or contact support."
          onClose={errorCloseHandler}
          actions={(
            <button
              onClick={errorCloseHandler}
              className="button"
            >
              Close
            </button>
          )}
        />
      )}
    </>
  );
};
